import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	data,
	redirect,
	type ActionFunctionArgs,
	type LinksFunction,
	type MetaFunction,
	Link,
	useFetcher,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/templates'
import { ErrorList, HouseMark } from '#app/components/atoms'
import { Field } from '#app/components/molecules/form'
import { StatusButton } from '#app/components/molecules'
import { prisma } from '#app/utils/db.server.ts'
import {
	EmailButton,
	EmailCode,
	EmailFine,
	EmailKicker,
	EmailLayout,
	EmailText,
	EmailTitle,
} from '#app/utils/email-layout.server.tsx'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import authStyleUrl from '../../styles/auth.css?url'
import { prepareVerification } from './verify.server.ts'
import { pageMeta } from '#app/utils/page-meta.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const links: LinksFunction = () => [
	{ rel: 'stylesheet', href: authStyleUrl },
]

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message:
						'Det finst ingen brukar med dette brukarnamnet eller denne e-posten',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
		select: { email: true, username: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})

	const response = await sendEmail({
		to: String(user.email),
		subject: `Huskonsertfestivalen - Endring av passord`,
		react: (
			<ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
		),
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

function ForgotPasswordEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<EmailLayout preview="Trykk på knappen eller skriv inn koden for å lage eit nytt passord.">
			<EmailKicker>Gløymt passordet?</EmailKicker>
			<EmailTitle>Lag eit nytt passord</EmailTitle>
			<EmailText>Trykk på knappen for å lage eit nytt passord:</EmailText>
			<EmailButton href={onboardingUrl}>Lag nytt passord</EmailButton>
			<EmailText>Eller skriv inn denne koden:</EmailText>
			<EmailCode code={otp} />
			<EmailFine>
				Koden gjeld i ti minutt. Bad du ikkje om eit nytt passord, kan du sjå
				bort frå denne e-posten: passordet ditt er som før.
			</EmailFine>
		</EmailLayout>
	)
}

export const meta: MetaFunction = (args) =>
	pageMeta(args, { title: 'Gløymt passord' })

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<main className="au">
			<div className="au-card">
				<div className="au-mark">
					<HouseMark />
				</div>
				<p className="au-kicker">Gløymt passordet?</p>
				<h1 className="au-title">Ikkje noko problem</h1>
				<p className="au-lede">
					Du treng eigentleg ikkje passord:{' '}
					<Link to="/login">logg inn med ein kode på e-post</Link>. Vil du
					heller lage eit nytt passord, sender vi deg ei lenkje.
				</p>
				<forgotPassword.Form
					method="POST"
					className="au-form"
					{...getFormProps(form)}
				>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.usernameOrEmail.id,
							children: 'Brukarnamn eller e-post',
						}}
						inputProps={{
							autoFocus: true,
							autoCapitalize: 'none',
							...getInputProps(fields.usernameOrEmail, { type: 'text' }),
						}}
						errors={fields.usernameOrEmail.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="au-submit"
						status={
							forgotPassword.state === 'submitting'
								? 'pending'
								: (form.status ?? 'idle')
						}
						type="submit"
						disabled={forgotPassword.state !== 'idle'}
					>
						Send lenkje
					</StatusButton>
				</forgotPassword.Form>
				<p className="au-fine">
					<Link to="/login" className="au-link">
						Tilbake til innlogging
					</Link>
				</p>
			</div>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
